import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import './Header.css'; // Import custom styles for the header
import SearchSidebar from '../SearchSidebar';
import httpService from "../../services/HttpService"; // Import your HttpService
import useIsMobile from '../../hooks/useIsMobile';
import { ReactComponent as CloseIcon } from '../../assets/icons/header/close.svg';
import { ReactComponent as MenuIcon  } from '../../assets/icons/header/menu.svg';
import { ReactComponent as BookMarkIcon  } from '../../assets/icons/header/bookmark.svg';
import { ReactComponent as PersonIcon  } from '../../assets/icons/header/person.svg';
import { ReactComponent as SearchIcon } from '../../assets/icons/header/search.svg';
import { ReactComponent as ShoppingBagIcon } from '../../assets/icons/header/shopping_bag.svg';
import { ReactComponent as ChevronLeft } from '../../assets/icons/header/chevron_left.svg';

const Header = forwardRef((props, ref) => {
    const [isNavOpen, setIsNavOpen] = useState(false);
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [subCategories, setSubCategories] = useState([]);
    const [activeCategoryTitle, setActiveCategoryTitle] = useState(null);
    const [activeCategoryId, setActiveCategoryId] = useState(null);
    const [cartCount, setCartCount] = useState(0);
    const [hoveredCategory, setHoveredCategory] = useState(null);
    const dropdownRef = useRef(null);
    const isMobile = useIsMobile();
    const [searchTerm, setSearchTerm] = useState('');
    const searchInputRef = useRef(null);
    const [searchResults, setSearchResults] = useState({ result_images: [], result_data: [] });
    const navigate = useNavigate();
    const location = useLocation();
    const isOnProductsPage = location.pathname === '/product';
    const [subCategoryCache, setSubCategoryCache] = useState({});
    const [isLoadingCategories, setIsLoadingCategories] = useState(false);
    const hostname = window.location.hostname;
    const domainConfig = {
        "zametna.com": {
            siteName: "zametna",
        },
        "dressme-platform.com": {
            siteName: "DressMe",
        }
    };

    useImperativeHandle(ref, () => ({
        incrementCart: () => setCartCount(prev => prev + 1),
        resetCart: () => setCartCount(0),
        updateCartCount: (count)=> setCartCount(count)
    }));

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);

        if (value.trim() === "") {
            setSearchResults({ result_images: [], result_data: [] });
            return;
        }
        fetchSearchResults(value);
    };

    const fetchSearchResults = async (query) => {
        try {
            const response = await httpService.get(`/search?text=${encodeURIComponent(query)}`);
            const data = await response.json();
            setSearchResults({
                result_images: data.result_images || [],
                result_data: data.result_data || [],
            });
        } catch (error) {
            console.error('Error fetching search results:', error);
        }
    };

    const clearSearch = () => {
        setSearchTerm("");
        setSearchResults({ result_images: [], result_data: [] });
    };

    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target) && searchInputRef.current && !searchInputRef.current.contains(event.target)) {
                clearHover();
                clearSearch();
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        clearHover();
        clearSearch();
    }, [location.search]);
    useEffect(() => {
        const fetchCart = async () => {
          try {
            const response = await httpService.get('/cart');
            let data = await response.json(); 
            setCartCount(data.length);
          } catch (error) {
            console.error('Failed to load cart data:', error);
          }
        };
      
        fetchCart();
    }, []);

    useEffect(() => {
        const scrollbarWidth = getScrollbarWidth();
        const isBodyScrollable = document.body.scrollHeight > window.innerHeight;
        const isFilterOpen = document.getElementsByClassName('filter-container open').length;
        if(isFilterOpen) return;
        if (isNavOpen || isSearchOpen || (hoveredCategory && subCategories.length > 0) || (searchTerm && !isResultsEmpty)) {
            document.body.style.overflow = "hidden";
            if (isBodyScrollable) {
                document.body.style.paddingRight = `${scrollbarWidth}px`;
            } else {
                document.body.style.paddingRight = "0px";
            }
        } else {
            document.body.style.overflow = "";
            document.body.style.paddingRight = "0px"; 
        }
    }, [isNavOpen, isSearchOpen, hoveredCategory, searchTerm, searchResults]);

    useEffect(() => {
        const fetchAllSubCategories = async () => {
            try {
                setIsLoadingCategories(true);
                const topCategoryIds = [1, 2, 3];
                const fetches = topCategoryIds.map(id =>
                    httpService.get(`/categories?parent_id=${id}`)
                    .then(res => res.json())
                    .then(data => ({ id, data }))
                );
                const results = await Promise.all(fetches);
                const cache = {};
                results.forEach(({ id, data }) => {
                    cache[id] = data;
                });
                setSubCategoryCache(cache);
            } catch (err) {
            console.error('Failed to preload subcategories:', err);
            } finally {
                setIsLoadingCategories(false);
            }
        };

        fetchAllSubCategories();
    }, []);

    const handleCategoryHover = (categoryId, title) => {
        const initialScroll = window.scrollY;
        if (searchTerm || (location.pathname !== '/product' && initialScroll > 65)) return;
      
        const cached = subCategoryCache[categoryId];
        if (!cached) return;
      
        const afterScroll = window.scrollY;
        if (afterScroll > initialScroll) return;
      
        setSubCategories(cached);
        setActiveCategoryTitle(title);
        setActiveCategoryId(categoryId);
        setHoveredCategory(categoryId);
      };      
      
      const clearHover = () => {
        setHoveredCategory(null);
        setSubCategories([]);
      };

      const handleMouseLeave = (e) => {
        const dropdown = document.querySelector('.category-dropdown-menu');
      
        const target = e.relatedTarget;
      
        const isSafeTarget =
          target &&
          target instanceof Element &&
          (dropdown?.contains(target) || target.closest('.category-span'));
      
        if (!isSafeTarget) {
          clearHover();
        }
      };

    const isResultsEmpty =
        searchResults.result_images.length === 0 &&
        searchResults.result_data.length === 0;

    function getScrollbarWidth() {
        const div = document.createElement("div");
        div.style.width = "100px";
        div.style.height = "100px";
        div.style.overflow = "scroll";
        document.body.appendChild(div);
        const scrollbarWidth = div.offsetWidth - div.clientWidth;
        document.body.removeChild(div);
        return scrollbarWidth;
    }

    const handleClick = () => {
        if (setIsSearchOpen) setIsSearchOpen(false);
        if (setIsNavOpen) setIsNavOpen(false);
    };
    const toggleNav = () => {
        setIsNavOpen(!isNavOpen);
    };

    const toggleSearch = () => {
        setIsSearchOpen(!isSearchOpen);
    };

    const closeNav = () => {
        setIsNavOpen(false);
    };

    const closeCategoriesModal = () => {
        setIsModalOpen(false);
        closeNav();
    };

    const getLinkPath = (item) => {
        const { type, id } = item;
        switch (type) {
            case 'model':
                return `/model/${id}`;
            case 'brand':
                return `/brand/${id}`;
            case 'product':
                return `/product/${id}`;
            case 'collection':
                return `/product?collection_id=${id}`;
            case 'category':
                return `/product?category_id=${id}`;
            default:
                return '#';
        }
    };

    const openCategoryModal = async (categoryId, activeCategoryTitle) => {
        try {
            const response = await httpService.get(`/categories?parent_id=${categoryId}`)
                .then(response => response.json()); // Use HttpService
            setSubCategories(response); // HttpService already parses JSON, so no need for .json()
            setActiveCategoryTitle(activeCategoryTitle);
            setActiveCategoryId(categoryId);
            setIsModalOpen(true);
        } catch (error) {
            console.error("Ошибка загрузки категорий:", error);
        }
    };

    return (
        <>
        {isMobile ? (
            <header className="header">
                <button
                    className="hamburger"
                    onClick={toggleNav}
                    aria-label="Toggle Navigation Menu"
                    style={{ display: isSearchOpen ? 'none' : '' }}
                >
                    {isNavOpen ? <CloseIcon /> : <MenuIcon />}
                </button>
                <button className="header-icon" onClick={toggleSearch}>
                    {isSearchOpen ?  <CloseIcon /> : <SearchIcon />}
                </button>
                <Link to="/home" className="logo-link" onClick={handleClick} style={{ marginLeft: isSearchOpen ? '24px' : '' }}>
                    <h1>{domainConfig[hostname]?.siteName ?? 'zametna'}</h1>
                </Link>
                <div className="header-buttons">
                    <Link to="/cart" className="link" onClick={closeNav}>
                        <button className="header-icon">
                            <ShoppingBagIcon />
                            {!!cartCount && (<span className="cart-items">{cartCount}</span>)}
                        </button>
                    </Link>
                    <Link to="/profile" className="link" onClick={closeNav}>
                        <button className="header-icon">
                            <PersonIcon />
                        </button>
                    </Link>
                </div>
            </header>
        ) : (
             <header className="desktop-header shadow">
                <div className="nav-left">
                    <div className="top-categories">
                        <Link to="/home">
                            <span style={{fontWeight: "bold"}}>Женское</span>
                        </Link>
                        <Link to="/home-сhild">
                            <span>Детское</span>
                        </Link>
                        {/*<span>Для дома</span>*/}
                    </div>
                    <div className="main-links">
                        <span className="category-span" onClick={() => navigate(`/product?category_id=${1}`)}
                              onMouseLeave={handleMouseLeave}
                              onMouseEnter={() => handleCategoryHover(1, 'Одежда')}>Одежда</span>
                        {/*<span className="category-span" onClick={() => navigate(`/product?category_id=${2}`)}*/}
                        {/*      onMouseLeave={handleMouseLeave}*/}
                        {/*      onMouseEnter={() => handleCategoryHover(2, 'Обувь')}>Обувь</span>*/}
                        <span className="category-span" onClick={() => navigate(`/product?category_id=${3}`)}
                              onMouseLeave={handleMouseLeave}
                              onMouseEnter={() => handleCategoryHover(3, 'Аксессуары')}>Аксессуары</span>
                        <span><Link to="/recommendations" onClick={closeNav}>Образы</Link></span>
                        <span><Link to="/brands" onClick={closeNav}>Бренды</Link></span>
                        {/*<span>*/}
                            {/*<Link to="/stylists" onClick={closeNav}>*/}
                            {/*Стилисты*/}
                            {/*</Link>*/}
                        {/*</span>*/}
                    </div>
                </div>

                 <div className="logo">
                     <Link to="/home" className="logo-link" onClick={handleClick}
                           style={{marginLeft: isSearchOpen ? '24px' : ''}}>
                         {domainConfig[hostname]?.siteName || 'zametna'}
                     </Link>
                 </div>
                 <div className="nav-right">
                     <div className="user-info">
                        <Link to="/cart" className="link" onClick={closeNav} style={{marginRight: '7px'}}>
                            <button className="header-icon">
                                <ShoppingBagIcon />
                                {!!cartCount && (<span className="cart-items">{cartCount}</span>)}
                            </button>
                        </Link>
                        <Link to="/profile" className="link" onClick={closeNav}>
                            <button className="header-icon">
                                <PersonIcon />
                            </button>
                        </Link>
                        <Link to="/saved" className="link" onClick={closeNav}>
                            <button className="header-icon">
                                <BookMarkIcon />
                            </button>
                        </Link>
                    </div>
                    <div className='search'>
                        <form onSubmit={(e) => e.preventDefault()} className="search-form">
                            <div className="search-input-wrapper">
                                <SearchIcon />
                                <input
                                    type="text"
                                    className="search-input"
                                    placeholder="Поиск"
                                    value={searchTerm}
                                    ref={searchInputRef}
                                    onChange={handleSearchChange}
                                />
                                {searchTerm && <span className="clear-icon" onClick={clearSearch}>✖</span>}
                            </div>
                        </form>
                    </div>
                </div>
                {hoveredCategory && subCategories.length > 0 && (
                    <div ref={dropdownRef} className="category-dropdown-menu" onMouseLeave={handleMouseLeave}>
                        <div className="burger-menu-category-section">
                            <Link to={`/product?category_id=${activeCategoryId}`} onClick={clearHover}><span className="burger-menu-category-section-title">Все категории</span></Link>
                        </div>
                        {subCategories.map((category, index) => (
                            <div key={index} className="burger-menu-category-section">
                                <Link to={`/product?category_id=${category.id}`} onClick={clearHover}><span className="burger-menu-category-section-title">{category.name}</span></Link>
                            </div>
                        ))}
                    </div>
                )}
                {(searchResults.result_data.length > 0 || searchResults.result_images.length > 0) && searchTerm && (
                    <div ref={dropdownRef} className='category-dropdown-menu for-search'>
                        {searchResults.result_images.length > 0 && (
                            <div className="result-images-container">
                                <div className="result-images">
                                    {searchResults.result_images.map((item, index) => (
                                        <a key={`${item.id}-${index}`} href={`/${item.type}/${item.id}`}>
                                            <img src={item.image} alt={item.name} className="result-image" />
                                        </a>
                                    ))}
                                </div>
                            </div>
                        )}

                        {searchResults.result_data.length > 0 && (
                            <div className="result-section">
                                {searchResults.result_data.map((item, index) => (
                                    <div
                                        key={`${item.id}-${index}`}
                                        className="result-item"
                                        onClick={() => navigate(getLinkPath(item))}
                                    >
                                        {item.name} <span className="chevron">›</span>
                                    </div>
                                ))}
                            </div>
                        )}

                        {searchTerm && searchResults.result_images.length === 0 && searchResults.result_data.length === 0 && (
                            <div className="no-results">Нет результатов</div>
                        )}
                    </div>
                )}
           </header>
        )}

            {/* Navigation Sidebar */}
            <nav className={`sidebar ${isNavOpen ? 'open' : ''}`}>
                <button
                    className="hamburger close"
                    onClick={toggleNav}
                    aria-label="Toggle Navigation Menu"
                    style={{display: isSearchOpen ? 'none' : ''}}
                >
                    {isNavOpen ? <CloseIcon /> : <MenuIcon />}
                </button>
                <div onClick={() => openCategoryModal(1, 'Одежда')} className="burger-menu-category-section">Одежда
                </div>
                {/*<div onClick={() => openCategoryModal(2, 'Обувь')} className="burger-menu-category-section">Обувь</div>*/}
                <div onClick={() => openCategoryModal(3, 'Аксессуары')}
                     className="burger-menu-category-section">Аксессуары
                </div>
                <div className="burger-menu-category-section"><Link to="/recommendations"
                                                                    onClick={closeNav}>Образы</Link></div>
                <div className="burger-menu-category-section"><Link to="/brands" onClick={closeNav}>Бренды</Link></div>
                {/*<div className="burger-menu-category-section">*/}
                    {/*<Link to="/stylists" onClick={closeNav}>Стилисты</Link>*/}
                {/*</div>*/}
                {/* <div onClick={() => openCategoryModal(4, 'Косметика')} className="burger-menu-category-section">Косметика</div> */}
                <CategoryModal activeCategoryTitle={activeCategoryTitle} isOpen={isModalOpen}
                               onClose={() => closeCategoriesModal()} back={() => setIsModalOpen(false)}
                               subCategories={subCategories} isNavOpen={isNavOpen} activeCategoryId={activeCategoryId}/>
                <div className={`overlay ${isNavOpen ? 'show' : ''}`} onClick={closeNav}></div>
                <SearchSidebar isSearchOpen={isSearchOpen} toggleSearch={toggleSearch}/>
                {((hoveredCategory && subCategories.length > 0) || (searchTerm && (searchResults.result_data.length > 0 || searchResults.result_images.length > 0))) &&
                    <div style={{top: `${(hoveredCategory ? 403 : 422) - (isOnProductsPage ? 0 : window.scrollY)}px`}}
                         className="category-sidebar-overlay"></div>}
                {(isNavOpen || isSearchOpen) &&
                    <div style={{left: isNavOpen ? '30%' : '50%'}} className="sidebar-overlay"
                         onClick={closeNav}></div>}
            </nav>
        </>
    );
});

const CategoryModal = ({activeCategoryTitle, isOpen, onClose, back, subCategories, isNavOpen, activeCategoryId}) => {
    // Use useEffect to handle state updates safely, preventing render-time state changes
    useEffect(() => {
        if (!isNavOpen) {
            onClose();
        }
    }, [isNavOpen, onClose]);

    if (!isOpen) {
        return null;
    }

    return (
        <div className={`burger-menu-modal-content`}>
            <div className='active-category-title'>
                <span onClick={back} className="material-symbols-sharp burger-menu-back-button"><ChevronLeft /></span>
                {activeCategoryTitle}
            </div>
            <div className="burger-menu-category-section">
                <Link to={`/product?category_id=${activeCategoryId}`} onClick={onClose}><span className="burger-menu-category-section-title">Все категории</span></Link>
            </div>
            {subCategories.map((category, index) => (
                <div key={index} className="burger-menu-category-section">
                    <Link to={`/product?category_id=${category.id}`} onClick={onClose}><span className="burger-menu-category-section-title">{category.name}</span></Link>
                </div>
            ))}
        </div>
    );
};

export default Header;